/* eslint-disable css-modules/no-undef-class, css-modules/no-unused-class */
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Button, LearningPanelNotification, Password, UsernameOrEmail } from 'learning-panel-components';
import { translate } from 'translations';
import styles from './login.module.scss';

declare const window: any;

const Login: FC<LoginProps> = (props: LoginProps) => {
    const { error, onLogin, isLoading, isPLG, onCreateNewAccount, onForgetPassword, onLoginToGoogle } = props;

    const [userNameOrEmail, setUserNameOrEmail] = useState<string>('');
    const [password, setPassword] = useState<string>('');

    const isEnable = !isLoading;

    const onSubmit = useCallback(() => {
        if (!isEnable) return;
        onLogin(userNameOrEmail, password);
    }, [onLogin, userNameOrEmail, password, isEnable]);

    useEffect(() => {
        const listener = (event: { code: string; preventDefault: () => void }) => {
            if (event.code === 'Enter' || event.code === 'NumpadEnter') {
                event.preventDefault();
                onSubmit();
            }
        };

        document.addEventListener('keydown', listener);
        return () => {
            document.removeEventListener('keydown', listener);
        };
    }, [isEnable, onSubmit, userNameOrEmail, password]);

    const loginWithGithub = () => {
        window.location.assign(
            `https://github.com/login/oauth/authorize?scope=user:email&client_id=${process.env.CB_GITHUB_CLIENT_ID}`
        );
    };

    const googleLoginCallback = (token: any) => {
        onLoginToGoogle(token.credential);
    };

    window.google?.accounts.id.initialize({
        client_id: process.env.CB_GOOGLE_CLIENT_ID,
        ux_mode: 'popup',
        callback: googleLoginCallback
    });

    const createFakeGoogleWrapper = () => {
        const googleLoginWrapper = document.createElement('div');
        googleLoginWrapper.style.display = 'none';
        googleLoginWrapper.classList.add('custom-google-button');
        document.body.appendChild(googleLoginWrapper);
        window.google?.accounts.id.renderButton(googleLoginWrapper, {
            type: 'icon',
            width: '200'
        });

        const googleLoginWrapperButton = googleLoginWrapper.querySelector('div[role=button]');

        return {
            click: () => {
                (googleLoginWrapperButton as any)?.click();
            }
        };
    };

    const googleButtonWrapper = createFakeGoogleWrapper();

    const loginWithGoogle = () => {
        googleButtonWrapper.click();
    };

    return (
        <div className={styles['login-wrapper']}>
            <div className={styles['login-text']}>{translate('Login')}</div>
            {isPLG && (
                <div className={styles.buttons}>
                    <Button
                        type="flatGray"
                        text={translate('Google')}
                        innerIconName="google"
                        onClick={loginWithGoogle}
                        lowerCase
                    />
                    <Button
                        type="flatGray"
                        text={translate('github')}
                        innerIconName="github"
                        onClick={loginWithGithub}
                        lowerCase
                    />
                    <div className={styles.or}>
                        <hr />
                        <div>{translate('orLoginWith')}</div>
                        <hr />
                    </div>
                </div>
            )}
            <div className={styles.inputs} style={{ '--marginTop': isPLG ? '2rem' : '4.5rem' } as any}>
                <div className={styles.section}>
                    <div className={styles['up-title']}>{translate('EmailAddress')}</div>
                    <UsernameOrEmail
                        usernameOrEmail={userNameOrEmail}
                        onChange={setUserNameOrEmail}
                        autofocus
                        id="user-name"
                    />
                </div>
                <div className={styles.section}>
                    <div className={styles['up-title']}>{translate('Password')}</div>
                    <Password password={password} onChangePassword={setPassword} id="password" />
                    <div className={styles['forget-password']} onClick={() => onForgetPassword()}>
                        {translate('forgotPassword')}
                    </div>
                </div>
            </div>
            <div className={styles['general-error-wrapper']}>
                {error && (
                    <LearningPanelNotification
                        notificationText={error}
                        hasClose={false}
                        variant="error"
                        handleClose={() => undefined}
                        id="login-error"
                    />
                )}
            </div>
            <div className="buttons">
                <Button
                    type={isEnable ? 'bigGradient' : 'bigGradientDisable'}
                    onClick={onSubmit}
                    text={translate('Login')}
                    isLoading={isLoading}
                    id="login-button"
                    lowerCase
                />
                {isPLG && (
                    <div
                        id="create-new-account-button"
                        className={styles['new-account']}
                        onClick={() => onCreateNewAccount?.()}
                    >
                        {translate('CreateNewAccount')}
                    </div>
                )}
            </div>
        </div>
    );
};

interface LoginProps {
    onLogin: Function;
    onCreateNewAccount?: Function;
    onForgetPassword: Function;
    error: string;
    isLoading?: boolean;
    isPLG: boolean;
    onLoginToGoogle: Function;
}

export default Login;
